import { useRef, useState } from "react";
import { toast } from "sonner";
import { Wand2, Upload, Loader2, ImageOff, Sparkles } from "lucide-react";
import { http, errMsg, API } from "../../lib/api";

const Slot = ({ kit, s, reload }) => {
  const [busy, setBusy] = useState("");
  const input = useRef(null);
  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); toast.success(kind === "ai" ? "New image generated" : "Image uploaded"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const upload = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    run("upload", () => http.post(`/kits/${kit.id}/images/${s.slot}/upload`, fd));
  };
  return (
    <div data-testid={`photo-slot-${s.slot}`} className="rounded-xl border border-stone-200 bg-white overflow-hidden card-lift">
      <div className="aspect-[3/2] bg-[#F7F5F0] relative">
        {s.image ? (
          <img src={`${API}/kits/${kit.id}/images/${s.slot}/file?v=${s.image.id}`} alt={s.section_title} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full grid place-items-center text-stone-600"><ImageOff size={28} /></div>
        )}
        {busy && <div className="absolute inset-0 bg-black/60 grid place-items-center"><Loader2 className="animate-spin text-[#9A7B2C]" /></div>}
        {s.image && (
          <span data-testid={`photo-source-${s.slot}`} className="absolute top-3 left-3 rounded-sm bg-black/60 px-2.5 py-0.5 text-[10px] font-mono-ui uppercase tracking-wider text-white">
            {s.image.source === "ai" ? "AI · illustrative" : "Your upload"}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="eyebrow">{s.slot === "cover" ? "Cover" : "Section opener"}</div>
        <div className="font-display text-lg text-[#2B2A26] truncate">{s.section_title}</div>
        <div className="flex gap-2 mt-3">
          <button data-testid={`photo-generate-${s.slot}`} disabled={!!busy} onClick={() => run("ai", () => http.post(`/kits/${kit.id}/images/${s.slot}/generate`, {}, { timeout: 120000 }))}
            className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-1.5 text-xs text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-colors">
            <Wand2 size={13} /> {s.image ? "Regenerate" : "Generate"}
          </button>
          <button data-testid={`photo-upload-${s.slot}`} disabled={!!busy} onClick={() => input.current.click()}
            className="inline-flex items-center gap-1.5 rounded-sm border border-[#C9A847]/40 px-3 py-1.5 text-xs text-[#9A7B2C] hover:bg-[#C9A847]/10 transition-colors">
            <Upload size={13} /> Upload my photo
          </button>
          <input ref={input} data-testid={`photo-file-input-${s.slot}`} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
};

export const PhotosTab = ({ kit, reload }) => {
  const slots = kit.photo_slots || [];
  const missing = slots.filter((s) => !s.image).length;
  const genMissing = async () => {
    try { await http.post(`/kits/${kit.id}/images/generate-missing`); toast.success("Generating missing photos — refresh in a minute"); setTimeout(reload, 45000); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-sm text-stone-500 max-w-2xl">These photos appear on the PDF cover, section openers and in the interactive app. AI images are labelled “Illustrative image” in the kit; your own uploads are not.</p>
        {missing > 0 && (
          <button data-testid="photos-generate-missing" onClick={genMissing} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-5 py-2 text-sm font-bold hover:bg-[#B8973A] transition-colors">
            <Sparkles size={15} /> Generate {missing} missing
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {slots.map((s) => <Slot key={s.slot} kit={kit} s={s} reload={reload} />)}
      </div>
    </div>
  );
};
